'use client';

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Aviso de datos incorrectos al centro de la pantalla: backdrop + tarjeta con un
// ícono de alerta que "rebota" al entrar — mismo lenguaje visual que el aviso de
// acceso concedido del login (LoginSuccessOverlay), en clave de error.
export default function AvisoErrorCentral({
  mensaje,
  titulo = 'Revise los datos',
  detalle,
  accion = 'Corregir',
  onCerrar,
}: {
  mensaje: string | null;
  titulo?: string;
  detalle?: string | null; // línea secundaria (p. ej. el mensaje técnico del servidor)
  accion?: string; // texto del botón (p. ej. «Aceptar» para errores del sistema)
  onCerrar: () => void;
}) {
  // Cerrar con Escape. Se escucha en `window` y en fase de captura A PROPÓSITO:
  // un diálogo de Radix abierto detrás escucha Escape en `document` (captura) y
  // se cerraría él en vez del aviso; window captura primero y aquí lo detenemos.
  useEffect(() => {
    if (!mensaje) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      onCerrar();
    };
    window.addEventListener('keydown', alTeclear, { capture: true });
    return () => window.removeEventListener('keydown', alTeclear, { capture: true });
  }, [mensaje, onCerrar]);

  return (
    <AnimatePresence>
      {mensaje && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          // `pointer-events-auto` es OBLIGATORIO: mientras un diálogo modal de Radix
          // está abierto, este pone `pointer-events: none` en el <body> y solo se los
          // devuelve a su propia capa. Este aviso vive en el layout raíz (fuera de esa
          // capa), así que sin esto se vería pero NO recibiría clics — y los clics
          // caerían en el diálogo de atrás, que sí los acepta.
          className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
          onClick={onCerrar}
          // Radix escucha el pointerdown en `document` para detectar "clic fuera" y
          // cerrar su diálogo: lo cortamos aquí para que usar este aviso no cierre el
          // diálogo que está detrás.
          onPointerDown={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="flex w-full max-w-sm flex-col items-center gap-4 rounded-3xl border border-white/60 bg-white/95 px-8 py-8 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-label={titulo}
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, delay: 0.08, ease: [0.34, 1.56, 0.64, 1] }}
              className="flex size-16 items-center justify-center rounded-full bg-destructive/10 text-destructive"
            >
              <AlertTriangle className="size-8" strokeWidth={2.5} />
            </motion.span>
            <div>
              <p className="text-xl font-bold tracking-tight text-slate-900">{titulo}</p>
              <p className="mt-1.5 text-sm text-slate-600">{mensaje}</p>
              {detalle && <p className="mt-1 text-xs text-slate-400">{detalle}</p>}
            </div>
            <Button type="button" className="w-full" onClick={onCerrar} autoFocus>
              {accion}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
